import React, { useState } from "react";
import { Sparkles, Loader2, ShieldQuestion, Plus, Trash2, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const GRADES = ["Low", "Medium", "High"].map((v) => ({ value: v, label: v }));
const KINDS = [{ value: "risk", label: "Risks" }, { value: "issue", label: "Issues" }];

/** Paste raw notes from a workshop or email, let AI classify them, then create real records. */
export const QuickTriage = ({ projects, projectId, setProjectId, onCreated }) => {
  const [kind, setKind] = useState("risk");
  const [text, setText] = useState("");
  const [rows, setRows] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [busy, setBusy] = useState("");

  const triage = async () => {
    if (!projectId) return toast.error("Choose a project first");
    if (!text.trim()) return toast.error("Paste at least one risk or issue");
    setBusy("triage");
    try {
      const { data } = await api.post("/assistant/triage", { project_id: projectId, kind, text });
      setTypeOptions(data.type_options || []);
      setRows(data.items.map((i) => ({ ...i, keep: true })));
      if (data.ai_error) toast.warning(data.ai_error);
      else toast.success(`${data.count} item${data.count === 1 ? "" : "s"} triaged — review and create`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const setRow = (idx, patch) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const create = async () => {
    const keep = (rows || []).filter((r) => r.keep && r.title.trim());
    if (!keep.length) return toast.error("Tick at least one item to create");
    setBusy("create");
    try {
      const { data } = await api.post("/assistant/triage/create", {
        project_id: projectId,
        items: keep.map((r) => ({
          kind: r.kind, title: r.title, description: r.description || null, type: r.type || null,
          probability: r.probability || null, impact: r.impact || null, severity: r.severity || null,
        })),
      });
      toast.success(`Created ${data.created} record${data.created === 1 ? "" : "s"} in the register`);
      setRows(null);
      setText("");
      onCreated?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const keptCount = (rows || []).filter((r) => r.keep).length;

  return (
    <div data-testid="quick-triage">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <label className="field-label">Project</label>
          <Select testId="triage-project-select" value={projectId} onChange={setProjectId}
            options={projects.map((p) => ({ value: p.id, label: p.name }))} placeholder="Select project" />
        </div>
        <div className="w-40">
          <label className="field-label">Capture as</label>
          <Select testId="triage-kind-select" value={kind} onChange={setKind} options={KINDS} placeholder="Select" />
        </div>
      </div>

      <label className="field-label mt-4 block">Paste your notes — one item per line</label>
      <Textarea
        data-testid="triage-input"
        rows={5}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"Vendor may miss the API delivery date\nContaminated soil found on the east elevation\nNo signed data-sharing agreement with the trust yet"}
      />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button data-testid="triage-btn" disabled={busy === "triage"} onClick={triage} className="gap-1.5"
          style={{ backgroundColor: "var(--brand-primary)" }}>
          {busy === "triage" ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Triage
        </Button>
        <span className="text-xs text-slate-500">
          Bullets and numbered lines are handled. Up to 25 items at a time. Nothing is saved until you create.
        </span>
      </div>

      {rows && (
        <div className="mt-5" data-testid="triage-results">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Review before creating — {keptCount} of {rows.length} selected
            </h4>
            <Button data-testid="triage-create-btn" disabled={busy === "create" || !keptCount} onClick={create} className="gap-1.5"
              style={{ backgroundColor: "var(--brand-primary)" }}>
              {busy === "create" ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
              Create {keptCount} {kind === "risk" ? "Risk" : "Issue"}{keptCount === 1 ? "" : "s"}
            </Button>
          </div>

          <div className="mt-3 space-y-3">
            {rows.map((r, idx) => (
              <div key={idx} data-testid={`triage-row-${idx}`}
                className={`rounded border p-3 transition-colors ${r.keep ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-60"}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    data-testid={`triage-keep-${idx}`}
                    checked={r.keep}
                    onChange={(e) => setRow(idx, { keep: e.target.checked })}
                    className="mt-2.5 h-4 w-4 shrink-0"
                  />
                  <div className="min-w-0 flex-1 space-y-2">
                    <Input data-testid={`triage-title-${idx}`} value={r.title}
                      onChange={(e) => setRow(idx, { title: e.target.value })} />
                    <div className="flex flex-wrap gap-2">
                      <div className="w-52">
                        <label className="field-label">Type</label>
                        <Select testId={`triage-type-${idx}`} value={r.type || ""} onChange={(v) => setRow(idx, { type: v })}
                          options={typeOptions.map((t) => ({ value: t, label: t }))} placeholder="Not set" />
                      </div>
                      {r.kind === "risk" ? (
                        <>
                          <div className="w-32">
                            <label className="field-label">Probability</label>
                            <Select testId={`triage-prob-${idx}`} value={r.probability || ""} onChange={(v) => setRow(idx, { probability: v })}
                              options={GRADES} placeholder="Not set" />
                          </div>
                          <div className="w-32">
                            <label className="field-label">Impact</label>
                            <Select testId={`triage-impact-${idx}`} value={r.impact || ""} onChange={(v) => setRow(idx, { impact: v })}
                              options={GRADES} placeholder="Not set" />
                          </div>
                        </>
                      ) : (
                        <div className="w-32">
                          <label className="field-label">Severity</label>
                          <Select testId={`triage-severity-${idx}`} value={r.severity || ""} onChange={(v) => setRow(idx, { severity: v })}
                            options={GRADES} placeholder="Not set" />
                        </div>
                      )}
                    </div>
                    {r.rationale && (
                      <p className="text-xs text-slate-500" data-testid={`triage-rationale-${idx}`}>
                        <ShieldQuestion size={11} className="mr-1 inline" />{r.rationale}
                      </p>
                    )}
                    {!r.classified && (
                      <p className="text-xs text-amber-700">Not classified by AI — set the fields yourself before creating.</p>
                    )}
                  </div>
                  <button data-testid={`triage-drop-${idx}`} onClick={() => setRows(rows.filter((_, i) => i !== idx))}
                    className="mt-2 shrink-0 text-slate-400 transition-colors hover:text-red-600" title="Discard">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
